import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import api, { trials, assessments, auth, onboarding } from '../utils/api';
import { Briefcase, LogOut, User, Calendar, Sparkles, ChevronDown, ChevronUp, Settings, Trash2, MapPin, CheckCircle, XCircle, Clock, Pencil, CreditCard, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import TalentCard from '../components/TalentCard';
import ShortviewTalentCard from '../components/ShortviewTalentCard';
import EditBookingModal from '../components/EditBookingModal';
import FoundingMemberFAB from '../components/FoundingMemberFAB';
import PageFooter from '../components/PageFooter';

// Status mapping: backend TrialStatus enum → dashboard categories
const STATUS_PENDING = ['pending_candidate_response', 'pending_payment'];
const STATUS_ACTIVE = ['scheduled', 'in_progress'];
const STATUS_COMPLETED = ['completed', 'terminated_early', 'cancelled', 'cancelled_late', 'expired'];

// Human-readable labels for each booking stage
const STATUS_LABELS = {
    pending_candidate_response: 'Action required',
    pending_payment: 'Awaiting employer payment',
    scheduled: 'Scheduled',
    in_progress: 'In Progress',
    completed: 'Completed',
    terminated_early: 'Ended Early',
    cancelled: 'Cancelled',
    cancelled_late: 'Cancelled (Late)',
    expired: 'Expired',
};

// Formats the 48-hour response window remaining on a pending invitation.
// Returns null once the deadline has passed (booking auto-expires server-side).
const formatResponseCountdown = (deadlineIso, now) => {
    if (!deadlineIso) return null;
    const deadline = new Date(deadlineIso).getTime();
    if (Number.isNaN(deadline)) return null;
    const msLeft = deadline - now;
    if (msLeft <= 0) return null;
    const hoursLeft = Math.floor(msLeft / 36e5);
    const minsLeft = Math.floor((msLeft % 36e5) / 60000);
    const urgent = hoursLeft < 6;
    const text = hoursLeft >= 1
        ? `${hoursLeft}h ${minsLeft}m left to respond`
        : `${minsLeft}m left to respond`;
    return { text, urgent };
};

const CandidateDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const [myTrials, setMyTrials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [assessmentStatus, setAssessmentStatus] = useState(null);
    const [statusError, setStatusError] = useState(false);
    const [talentCard, setTalentCard] = useState(null);
    const [cardExpanded, setCardExpanded] = useState(false);
    const unlockKey = `sampler_card_unlocked_${user?.user_id || user?.id || 'anon'}`;
    const [cardUnlocked, setCardUnlocked] = useState(() => localStorage.getItem(unlockKey) === 'true');
    const [unlockAnimating, setUnlockAnimating] = useState(false);
    const [unlockPhase, setUnlockPhase] = useState('preparing');
    const [showFullBreakdown, setShowFullBreakdown] = useState(false);
    const [showAccountMenu, setShowAccountMenu] = useState(false);
    const [showDeleteAccount, setShowDeleteAccount] = useState(false);
    const [deletingAccount, setDeletingAccount] = useState(false);
    const [titleTagPopover, setTitleTagPopover] = useState(null);
    const [cancelModal, setCancelModal] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [editBookingModal, setEditBookingModal] = useState(null);
    const [editing, setEditing] = useState(false);
    // Stripe Connect modal state
    const [connectModal, setConnectModal] = useState(false);
    const [connectStatus, setConnectStatus] = useState(null);
    const [connectLoading, setConnectLoading] = useState(false);
    // Ticks every minute so the pending-invitation response countdown stays live
    const [now, setNow] = useState(() => Date.now());

    const accountMenuRef = useRef(null);
    const popoverRef = useRef(null);
    const unlockTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    // #6: useEffect with empty dep array — only run once on mount
    useEffect(() => {
        loadData();
    }, []);

    // Stripe Connect Account Link can expire mid-onboarding (link is single-use /
    // time-limited); Stripe then redirects back here with ?connect=refresh instead
    // of ?connect=done. Surface that clearly and open the setup modal so the
    // candidate can immediately retry, rather than landing on an unchanged
    // dashboard with no explanation.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('connect') === 'refresh') {
            toast.error('Your payout setup link expired before you finished. Please try again.');
            setConnectModal(true);
            navigate(location.pathname, { replace: true });
        }
    }, [location.search, location.pathname, navigate]);

    // Track mount status so async callbacks (setTimeout, fetches) can no-op
    // after unmount, preventing "setState on unmounted component" warnings.
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (unlockTimerRef.current) {
                clearTimeout(unlockTimerRef.current);
                unlockTimerRef.current = null;
            }
        };
    }, []);

    // Keeps the "time left to respond" countdown on pending invitations live
    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(interval);
    }, []);

    // #9: Outside-click handlers for account menu and title tag popover.
    // The returned cleanup removes the previously-attached listener before
    // the next effect run, so document listeners never accumulate.
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e || !e.target) return;
            if (showAccountMenu && accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
                setShowAccountMenu(false);
            }
            if (titleTagPopover && popoverRef.current && !popoverRef.current.contains(e.target)) {
                setTitleTagPopover(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAccountMenu, titleTagPopover]);

    const loadData = async () => {
        // #11: Separate status fetch with proper error handling
        let status = null;
        let statusFetchFailed = false;
        try {
            const assessmentRes = await assessments.getStatus();
            status = assessmentRes.data;
            setAssessmentStatus(status);
            setStatusError(false);
        } catch (error) {
            statusFetchFailed = true;
            setStatusError(true);
            setAssessmentStatus(null);
            toast.error('Failed to load assessment status. Please refresh the page.');
        }

        // #2: Always fetch trials regardless of cached assessment status.
        // Pending invitations must always be visible — even if the assessment
        // status cache is stale or the status call failed entirely.
        try {
            const trialsRes = await trials.getMyBookings();
            setMyTrials(trialsRes.data.bookings || []);
        } catch (error) {
            console.error('Failed to load bookings:', error);
        }

        if (status?.completed) {
            try {
                const talentCardRes = await assessments.getMyAssessment();
                if (talentCardRes.data.completed && talentCardRes.data.assessment?.talent_card) {
                    setTalentCard(talentCardRes.data.assessment.talent_card);
                    // Onboarding funnel tracking (admin-only) — fire-and-forget.
                    // Fires every time the dashboard loads with a completed
                    // Talent Card; dedup is handled server-side so only the
                    // first-ever view is actually recorded.
                    onboarding.track('talent_card_viewed');
                }
            } catch (e) {
                console.error('Failed to load talent card:', e);
            }
        }

        // Fetch Stripe Connect status silently
        try {
            const connectRes = await api.get('/candidate/stripe-connect/status');
            setConnectStatus(connectRes.data);
        } catch (_) {}

        setLoading(false);
        return statusFetchFailed;
    };

    const handleConnectOnboard = async () => {
        setConnectLoading(true);
        try {
            const res = await api.post('/candidate/stripe-connect/onboard');
            window.location.href = res.data.onboarding_url;
        } catch (e) {
            toast.error(e.userMessage || 'Could not start payout setup. Please try again.');
            setConnectLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
        toast.success('Logged out successfully');
    };

    const handleDeleteAccount = async () => {
        setDeletingAccount(true);
        try {
            await auth.deleteAccount();
            logout();
            navigate('/');
            toast.success('Account deleted successfully');
        } catch (error) {
            toast.error(error.userMessage || 'Failed to delete account');
        } finally {
            setDeletingAccount(false);
            setShowDeleteAccount(false);
        }
    };

    // #7: Per-user unlock key. The setTimeout is tracked in a ref and
    // cleared on unmount (see mount-status useEffect above) to avoid
    // setState calls on an unmounted component.
    const handleUnlockCard = useCallback(() => {
        setUnlockPhase('preparing');
        setUnlockAnimating(true);
        if (unlockTimerRef.current) {
            clearTimeout(unlockTimerRef.current);
        }
        // Stage 1 (preparing) -> Stage 2 (spinning) at 2000ms
        unlockTimerRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;
            setUnlockPhase('spinning');
            // Stage 2 (spinning) -> Stage 3 (revealed) after another 800ms
            unlockTimerRef.current = setTimeout(() => {
                unlockTimerRef.current = null;
                if (!isMountedRef.current) return;
                setCardUnlocked(true);
                localStorage.setItem(unlockKey, 'true');
                setUnlockAnimating(false);
            }, 800);
        }, 2000);
    }, [unlockKey]);

    const handleProfileBlockClick = () => {
        if (!talentCard) return;
        setCardExpanded(prev => !prev);
    };

    // #1: Use correct API method for candidate respond
    const handleRespondToBooking = async (bookingId, action) => {
        try {
            await trials.candidateRespond(bookingId, action);
            toast.success(action === 'accept' ? 'Invitation accepted!' : 'Invitation declined');
            loadData();
        } catch (error) {
            toast.error(error.userMessage || `Failed to ${action} invitation`);
        }
    };

    const handleCancelConfirm = async () => {
        if (!cancelModal) return;
        setCancelling(true);
        try {
            await trials.candidateCancel(cancelModal.id, '');
            toast.success('Trial cancelled');
            setCancelModal(null);
            await loadData();
        } catch (error) {
            toast.error(error.userMessage || 'Failed to cancel trial');
        } finally {
            setCancelling(false);
        }
    };

    const handleEditSubmit = async (changes, message) => {
        if (!editBookingModal) return;
        setEditing(true);
        try {
            await trials.requestEdit(editBookingModal.id, changes, message);
            toast.success('Edit request sent — the employer will see it on their dashboard.');
            setEditBookingModal(null);
            await loadData();
        } catch (err) {
            toast.error(err.userMessage || 'Failed to send edit request');
        } finally {
            setEditing(false);
        }
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0FDF4' }}>
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#8B5CF6] border-t-transparent" />
        </div>
    );

    // #2: Status filters using actual TrialStatus enum values
    const pendingTrials = myTrials.filter(t => STATUS_PENDING.includes(t.status));
    const activeTrials = myTrials.filter(t => STATUS_ACTIVE.includes(t.status));
    const completedTrials = myTrials.filter(t => STATUS_COMPLETED.includes(t.status));

    const stats = [
        { label: 'Action Required', value: myTrials.filter(t => t.status === 'pending_candidate_response').length, icon: Calendar, color: '#EC4899' },
        { label: 'Active Trials', value: activeTrials.length, icon: Briefcase, color: '#8B5CF6' },
        { label: 'Completed', value: completedTrials.length, icon: CheckCircle, color: '#10B981' },
    ];

    const header = talentCard?.header || {};
    const titleTag = talentCard?.title_tag || null;
    const strengthTags = talentCard?.strength_tags || {};
    const wildcard = talentCard?.wildcard || {};
    const isWildcard = wildcard?.is_wildcard === true;

    const tagChips = [];
    if (titleTag?.title) tagChips.push({ key: 'title', label: titleTag.title, evidence: titleTag.tooltip || titleTag.evidence || titleTag.description || 'Based on your assessment responses, this title reflects your strongest observed working pattern.' });
    if (strengthTags?.top_tag?.name) tagChips.push({ key: 'top', label: strengthTags.top_tag.name, evidence: strengthTags.top_tag.evidence || strengthTags.top_tag.description || 'Your most prominent strength observed across multiple assessment dimensions.' });
    if (strengthTags?.secondary_tag?.name) tagChips.push({ key: 'secondary', label: strengthTags.secondary_tag.name, evidence: strengthTags.secondary_tag.evidence || strengthTags.secondary_tag.description || 'A secondary strength pattern observed in your assessment responses.' });

    // #8: Optional chaining on user?.name throughout
    const candidateForCard = talentCard ? {
        id: user?.user_id || user?.id || 'self',
        name: header.name || user?.name || 'You',
        talent_card: talentCard
    } : null;

    // Shared trial card renderer
    const renderTrialCard = (trial, showActions = false) => (
        <div key={trial.id} className="bg-white rounded-2xl p-6 border border-purple-200/50 shadow-sm" data-testid={`trial-card-${trial.id}`}>
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    {/* #3: Use flat fields from booking documents */}
                    <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>{trial.job_title || 'Trial Shift'}</h3>
                    {trial.location && (
                        <p className="text-slate-500 font-medium mb-3 flex items-center gap-1" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            <MapPin className="h-3.5 w-3.5" /> {trial.location}
                        </p>
                    )}
                    <div className="flex items-center space-x-4 text-sm mb-2">
                        <span className="text-slate-500" style={{ fontFamily: 'Unbounded, sans-serif' }}>{trial.trial_days || trial.trial_length_days} days</span>
                        {trial.start_date && <span className="text-slate-400">Starts {trial.start_date}</span>}
                        {trial.start_time && <span className="text-slate-400">at {trial.start_time} (UK time)</span>}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            STATUS_ACTIVE.includes(trial.status) ? 'bg-purple-100 text-purple-700' :
                            ['completed'].includes(trial.status) ? 'bg-green-100 text-green-700' :
                            ['cancelled', 'cancelled_late', 'expired', 'terminated_early'].includes(trial.status) ? 'bg-slate-100 text-slate-600' :
                            'bg-amber-100 text-amber-700'
                        }`} data-testid={`trial-status-${trial.id}`}>
                            {STATUS_LABELS[trial.status] || trial.status}
                        </span>
                    </div>
                </div>
                {showActions && trial.status === 'pending_candidate_response' && (
                    <div className="flex space-x-2 ml-4">
                        <Button className="btn-primary" onClick={() => handleRespondToBooking(trial.id, 'accept')} data-testid={`accept-btn-${trial.id}`}>
                            Accept
                        </Button>
                        <Button variant="outline" className="rounded-full border-purple-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                            onClick={() => handleRespondToBooking(trial.id, 'decline')} data-testid={`decline-btn-${trial.id}`}>
                            Decline
                        </Button>
                    </div>
                )}
                {showActions && trial.status === 'pending_payment' && (
                    <div className="ml-4 text-right">
                        <p className="text-xs text-slate-500" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            You've accepted —<br/>employer is paying
                        </p>
                    </div>
                )}
                {trial.status === 'scheduled' && (
                    <div className="ml-4 flex flex-col items-end gap-2">
                        <Button variant="outline" className="rounded-full border-purple-200 text-[#7C3AED] hover:bg-purple-50 text-sm"
                            onClick={() => setEditBookingModal(trial)} data-testid={`request-edit-btn-${trial.id}`}>
                            <Pencil className="h-3.5 w-3.5 mr-1" /> Request Edit
                        </Button>
                        <Button variant="outline" className="rounded-full border-purple-200 text-slate-600 hover:border-red-300 hover:bg-red-50 hover:text-red-600 text-sm"
                            onClick={() => setCancelModal(trial)} data-testid={`cancel-btn-${trial.id}`}>
                            Cancel
                        </Button>
                    </div>
                )}
            </div>
            {/* Response countdown — 48hr window on pending candidate invitations */}
            {trial.status === 'pending_candidate_response' && (() => {
                const countdown = formatResponseCountdown(trial.response_deadline, now);
                if (!countdown) return null;
                return (
                    <div
                        className={`mt-3 rounded-xl p-3 flex items-center gap-2 border ${countdown.urgent ? 'bg-red-50 border-red-300/60' : 'bg-amber-50 border-amber-300/60'}`}
                        data-testid={`response-countdown-${trial.id}`}
                    >
                        <Clock className={`h-4 w-4 shrink-0 ${countdown.urgent ? 'text-red-600' : 'text-amber-600'}`} strokeWidth={2.5} />
                        <p className={`text-xs font-semibold ${countdown.urgent ? 'text-red-700' : 'text-amber-700'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            {countdown.text}
                        </p>
                    </div>
                );
            })()}
            {/* Payout-paused banner — fires when a Stripe transfer fails */}
            {trial.payout_paused && (
                <div className="mt-3 rounded-xl bg-amber-50 border border-amber-300/60 p-3 flex items-start gap-2" data-testid={`payout-paused-banner-${trial.id}`}>
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" strokeWidth={2.5} />
                    <div className="flex-1">
                        <p className="text-xs font-semibold text-amber-700 mb-0.5" style={{ fontFamily: 'Unbounded, sans-serif' }}>Payout on hold</p>
                        <p className="text-xs text-amber-700 leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            {trial.payout_paused_reason === 'identity_verification_required'
                                ? 'Stripe needs to verify your identity before releasing your payment. Complete your payout setup to resolve this.'
                                : 'Your payout could not be processed. Please complete your payout setup or contact support.'}
                        </p>
                        <button
                            className="mt-1.5 text-xs font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-900"
                            onClick={() => setConnectModal(true)}
                            style={{ fontFamily: 'Unbounded, sans-serif' }}
                            data-testid={`payout-paused-resolve-btn-${trial.id}`}
                        >
                            Complete payout setup
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <header className="sticky top-0 z-50 bg-[#F0FDF4]/80 backdrop-blur-xl border-b border-purple-200/50 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
                        <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                        <span className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Sampler</span>
                    </div>
                    <div className="flex items-center space-x-3">
                        <Button variant="ghost" className="text-slate-600 hover:text-[#8B5CF6] hover:bg-purple-50" onClick={() => navigate('/candidate/profile')} data-testid="profile-btn">
                            <User className="h-5 w-5" strokeWidth={2} />
                        </Button>
                        {/* #12: Messages icon removed — stub page not built */}
                        <div className="relative" ref={accountMenuRef}>
                            <Button variant="ghost" className="text-slate-600 hover:text-[#8B5CF6] hover:bg-purple-50" onClick={(e) => { e.stopPropagation(); setShowAccountMenu(!showAccountMenu); }} data-testid="account-settings-btn">
                                <Settings className="h-5 w-5" strokeWidth={2} />
                            </Button>
                            {showAccountMenu && (
                                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-purple-200/50 py-2 z-50">
                                    <button onClick={() => { setShowAccountMenu(false); handleLogout(); }}
                                        className="w-full px-4 py-2.5 text-left text-sm text-slate-600 hover:bg-purple-50 hover:text-[#8B5CF6] flex items-center gap-2"
                                        data-testid="menu-logout">
                                        <LogOut className="h-4 w-4" /> Log Out
                                    </button>
                                    <button onClick={() => { setShowAccountMenu(false); setShowDeleteAccount(true); }}
                                        className="w-full px-4 py-2.5 text-left text-sm text-red-500 hover:bg-red-50 flex items-center gap-2"
                                        data-testid="menu-delete-account">
                                        <Trash2 className="h-4 w-4" /> Delete Account
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 md:px-8 pt-10 pb-28">
                {/* Welcome — #8: optional chaining */}
                <div className="mb-10">
                    <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                        Candidate Dashboard
                    </span>
                    <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }} data-testid="dashboard-heading">
                        Welcome, {user?.name || 'Candidate'}
                    </h1>
                    <p className="text-slate-500 mt-1" style={{ fontFamily: 'Barlow, sans-serif' }}>Find trial opportunities and manage your applications</p>
                </div>

                {/* #11: Status error state */}
                {statusError && (
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8 text-center" data-testid="status-error">
                        <p className="text-red-600 font-medium">Failed to load your assessment status.</p>
                        <Button className="mt-3 rounded-full" onClick={() => { setStatusError(false); setLoading(true); loadData(); }}>
                            Try Again
                        </Button>
                    </div>
                )}

                {/* #10: Fallback for completed:false, required:false */}
                {assessmentStatus && !assessmentStatus.completed && !assessmentStatus.required && !statusError && (
                    <div className="bg-purple-50 border border-purple-200 rounded-2xl p-8 mb-8 text-center" data-testid="assessment-not-required">
                        <Sparkles className="h-10 w-10 mx-auto text-[#9333EA] mb-4" />
                        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Assessment Not Required</h2>
                        <p className="text-slate-600" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            Your account does not currently require an assessment. If you believe this is an error, please contact support.
                        </p>
                    </div>
                )}

                {/* Assessment prompt — only when status is loaded and assessment incomplete + required */}
                {assessmentStatus && !assessmentStatus.completed && assessmentStatus.required && (
                    <div className="flex items-center justify-center py-16" data-testid="assessment-prompt">
                        <div className="w-full max-w-2xl text-center">
                            <div className="rounded-3xl p-6 sm:p-10 md:p-14 relative border border-purple-200/50 shadow-xl" style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #4C1D95 100%)' }}>
                                <div className="rounded-2xl p-4 bg-white/10 backdrop-blur-sm w-20 h-20 flex items-center justify-center mx-auto mb-8">
                                    <Sparkles className="h-10 w-10 text-[#34D399]" />
                                </div>
                                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                    Generate Your Talent Card
                                </h2>
                                <p className="text-purple-200 text-lg mb-3 leading-relaxed max-w-lg mx-auto" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                    This is how employers discover you on Sampler.
                                </p>
                                <p className="text-purple-300 mb-8 max-w-lg mx-auto" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                    Complete a quick assessment covering your skills, sector experience, and work style.
                                    Our AI then builds a personalised Talent Card that showcases who you are — no CV needed.
                                </p>
                                <Button 
                                    className="rounded-full bg-white text-[#4C1D95] hover:bg-purple-50 font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 text-sm sm:text-base px-4 sm:px-8 py-3 h-auto whitespace-normal sm:whitespace-nowrap"
                                    onClick={() => navigate('/candidate/assessment')}
                                    data-testid="start-assessment-btn"
                                >
                                    <Sparkles className="mr-2 h-5 w-5 shrink-0" /> Let's Go — Takes 15-20 Minutes
                                </Button>
                                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-center">
                                    {['Select your sectors & roles', 'Complete quick micro-tests', 'AI generates your Talent Card'].map((text, i) => (
                                        <div key={i} className="flex sm:flex-col items-center sm:items-center gap-3 sm:gap-0 justify-center sm:justify-center">
                                            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>{i + 1}</p>
                                            <p className="text-sm sm:text-xs text-purple-300 sm:mt-1 text-left sm:text-center" style={{ fontFamily: 'Unbounded, sans-serif' }}>{text}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Dashboard content when assessment is complete */}
                {assessmentStatus?.completed && (<>

                {/* Activity Bar */}
                <div className="rounded-2xl p-4 sm:p-6 md:p-8 mb-4" style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #4C1D95 100%)' }} data-testid="activity-bar">
                    <div className="grid grid-cols-3 gap-2 sm:gap-6">
                        {stats.map((s, i) => {
                            const Icon = s.icon;
                            const hasPending = i === 0 && s.value > 0;
                            return (
                                <div key={i} className="min-w-0" data-testid={['pending-invites-stat', 'active-trials-stat', 'completed-trials-stat'][i]}>
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <div className="relative shrink-0">
                                            <Icon className="h-4 w-4" style={{ color: s.color }} strokeWidth={2.5} />
                                            {hasPending && (
                                                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#EC4899] border-2 border-[#1E1B4B] animate-pulse" data-testid="pending-notification-dot" />
                                            )}
                                        </div>
                                        <span className="text-[10px] sm:text-xs font-semibold text-purple-300 uppercase tracking-tight sm:tracking-wider leading-tight break-words" style={{ fontFamily: 'Unbounded, sans-serif' }}>{s.label}</span>
                                    </div>
                                    <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>{s.value}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* #4: Action Buttons with correct scroll targets */}
                <div className="grid grid-cols-3 gap-3 mb-8" data-testid="action-buttons">
                    <button
                        onClick={() => document.getElementById('section-pending')?.scrollIntoView({ behavior: 'smooth' })}
                        className="relative px-4 py-3 rounded-xl text-sm font-semibold bg-white border border-purple-200/50 text-slate-700 hover:border-[#9333EA] hover:bg-purple-50 transition-all shadow-sm"
                        data-testid="nav-pending-invites"
                    >
                        {pendingTrials.length > 0 && (
                            <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-[#EC4899] animate-pulse" />
                        )}
                        Pending Invites
                    </button>
                    <button
                        onClick={() => document.getElementById('section-active')?.scrollIntoView({ behavior: 'smooth' })}
                        className="px-4 py-3 rounded-xl text-sm font-semibold bg-white border border-purple-200/50 text-slate-700 hover:border-[#9333EA] hover:bg-purple-50 transition-all shadow-sm"
                        data-testid="nav-active-trials"
                    >
                        Active Trials
                    </button>
                    <button
                        onClick={() => document.getElementById('section-completed')?.scrollIntoView({ behavior: 'smooth' })}
                        className="px-4 py-3 rounded-xl text-sm font-semibold bg-white border border-purple-200/50 text-slate-700 hover:border-[#9333EA] hover:bg-purple-50 transition-all shadow-sm"
                        data-testid="nav-completed"
                    >
                        Completed
                    </button>
                </div>

                {/* Roles — Coming Soon placeholder section (Founding 1000 beta) */}
                <div className="mb-8" data-testid="roles-coming-soon">
                    <div
                        className="rounded-2xl p-6 pb-12 sm:pb-8 md:p-8 border border-purple-200/60 bg-white relative overflow-hidden"
                        style={{
                            background: 'linear-gradient(135deg, #FFFFFF 0%, #FAF5FF 60%, #F3E8FF 100%)'
                        }}
                    >
                        <div
                            aria-hidden="true"
                            className="absolute -right-12 -top-12 w-44 h-44 rounded-full pointer-events-none"
                            style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.18) 0%, rgba(139,92,246,0) 70%)' }}
                        />
                        <div className="relative">
                            <div className="flex items-center gap-2 mb-3">
                                <Sparkles className="h-4 w-4" style={{ color: '#8B5CF6' }} />
                                <span className="uppercase tracking-[0.2em] text-xs font-semibold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#8B5CF6' }}>
                                    Founding 1000
                                </span>
                            </div>
                            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                                Roles &mdash; Coming Soon
                            </h2>
                            <p className="text-base leading-relaxed max-w-2xl" style={{ fontFamily: 'Barlow, sans-serif', color: '#475569' }}>
                                Employers are being onboarded now. When they&rsquo;re live, matched roles will appear here based on your Talent Card. You won&rsquo;t need to apply &mdash; they&rsquo;ll come to you.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Profile Block */}
                {talentCard && (
                    <div className="mb-8" data-testid="talent-card-section">
                        <div 
                            className="bg-white rounded-2xl p-6 border border-purple-200/50 shadow-sm cursor-pointer hover:shadow-md hover:border-purple-300 transition-all duration-200 select-none"
                            onClick={handleProfileBlockClick}
                            data-testid="profile-block"
                        >
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div className="flex items-center space-x-4 min-w-0">
                                    <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: '#9333EA' }}>
                                        <User className="h-7 w-7 text-white" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                                            {header.name || user?.name || 'Candidate'}
                                        </h3>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-sm font-semibold text-[#10B981]">{header.primary_role || 'Role'}</span>
                                            {header.location && (
                                                <span className="text-xs text-slate-400 flex items-center">
                                                    <MapPin className="h-3 w-3 mr-0.5" />{header.location}
                                                </span>
                                            )}
                                        </div>
                                        {/* Tags as inline clickable chips — #9: ref for outside click */}
                                        <div className="flex items-center gap-2 mt-2 flex-wrap relative" ref={popoverRef}>
                                            {header.phase_badge && (
                                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-purple-600 to-pink-500 text-white">
                                                    {header.phase_badge}
                                                </span>
                                            )}
                                            {tagChips.map(chip => (
                                                <button
                                                    key={chip.key}
                                                    className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-[#F3E8FF] text-[#9333EA] hover:bg-[#EDE9FE] transition-colors"
                                                    onClick={(e) => { e.stopPropagation(); setTitleTagPopover(titleTagPopover === chip.key ? null : chip.key); }}
                                                    data-testid={`tag-chip-${chip.key}`}
                                                >
                                                    {chip.label}
                                                </button>
                                            ))}
                                            {titleTagPopover && tagChips.find(c => c.key === titleTagPopover) && (
                                                <div 
                                                    className="absolute left-0 top-full mt-2 z-50 bg-white rounded-xl shadow-lg border border-purple-200/50 p-4 max-w-xs"
                                                    onClick={(e) => e.stopPropagation()}
                                                    data-testid="title-tag-popover"
                                                >
                                                    <p className="font-bold text-sm text-[#9333EA] mb-1">{tagChips.find(c => c.key === titleTagPopover).label}</p>
                                                    <p className="text-xs text-slate-600 leading-relaxed">
                                                        {tagChips.find(c => c.key === titleTagPopover).evidence}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 justify-between sm:justify-end shrink-0">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!talentCard) return;
                                            setCardExpanded(true);
                                            setTimeout(() => {
                                                document.querySelector('[data-testid="talent-card-expanded"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            }, 100);
                                        }}
                                        data-testid="view-talent-card-btn"
                                        style={{
                                            background: '#8B5CF6',
                                            color: '#FFFFFF',
                                            border: 'none',
                                            borderRadius: '999px',
                                            padding: '10px 20px',
                                            fontFamily: 'Unbounded, sans-serif',
                                            fontSize: '0.8rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.01em',
                                            cursor: 'pointer',
                                            boxShadow: '0 6px 18px rgba(139, 92, 246, 0.35)',
                                            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                            whiteSpace: 'nowrap'
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                                    >
                                        View Talent Card
                                    </button>
                                    <div className="text-slate-400">
                                        {cardExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Expanded Talent Card Area */}
                        {cardExpanded && (
                            <div className="mt-4 transition-all duration-300" data-testid="talent-card-expanded">
                                {!cardUnlocked && !unlockAnimating && (
                                    <div className="relative rounded-2xl overflow-hidden" data-testid="card-locked">
                                        <div className="filter blur-md pointer-events-none flex justify-center py-8 px-4">
                                            <ShortviewTalentCard candidate={candidateForCard} isWildcard={isWildcard} alignmentScore={null} />
                                        </div>
                                        <div className="absolute inset-0 flex items-center justify-center bg-white/30 backdrop-blur-sm rounded-2xl">
                                            <Button
                                                className="rounded-full bg-[#9333EA] hover:bg-[#7C3AED] text-white font-bold text-lg px-8 py-4 h-auto shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5"
                                                onClick={handleUnlockCard}
                                                data-testid="unlock-card-btn"
                                            >
                                                <Sparkles className="mr-2 h-5 w-5" /> Unlock Your Card
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                {unlockAnimating && (
                                    <div className="flex items-center justify-center py-16" data-testid="card-unlocking">
                                        {unlockPhase === 'preparing' ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
                                                <div style={{ width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'sampler-pulse 1.2s ease-in-out infinite' }}>
                                                    <img src="/sampler-mark.png" alt="Sampler" style={{ height: 72, width: 'auto', display: 'block', filter: 'drop-shadow(0 0 24px rgba(124,58,237,0.6))' }} />
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontFamily: 'Unbounded, sans-serif', fontSize: 16, fontWeight: 800, color: '#fff', marginBottom: 8 }}>Building your Talent Card...</div>
                                                    <div style={{ fontFamily: 'Barlow, sans-serif', fontSize: 13, color: '#6B21A8' }}>Analysing your assessment responses</div>
                                                </div>
                                                <div style={{ width: 220, height: 3, background: '#1E0A38', borderRadius: 2, overflow: 'hidden' }}>
                                                    <div style={{ height: '100%', background: 'linear-gradient(90deg, #4C1D95, #7C3AED, #A78BFA)', borderRadius: 2, animation: 'sampler-loadbar 1.9s cubic-bezier(0.4,0,0.2,1) forwards' }} />
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ perspective: '1200px' }}>
                                                <div style={{ animation: 'sampler-spinreveal 0.8s cubic-bezier(0.4,0,0.2,1) forwards', transformStyle: 'preserve-3d' }}>
                                                    <ShortviewTalentCard candidate={candidateForCard} isWildcard={isWildcard} alignmentScore={null} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {cardUnlocked && !unlockAnimating && (
                                    <div data-testid="card-unlocked">
                                        {isWildcard && (
                                            <div className="mb-4 rounded-2xl p-5 border-2" style={{ borderColor: '#94a3b8', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)', boxShadow: '0 0 20px rgba(148, 163, 184, 0.3)' }} data-testid="wildcard-reveal">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <div className="p-2 rounded-lg" style={{ background: 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)' }}>
                                                        <Sparkles className="h-5 w-5 text-slate-600" />
                                                    </div>
                                                    <span className="font-bold text-slate-700" style={{ fontFamily: 'Unbounded, sans-serif' }}>Potential Detected</span>
                                                </div>
                                                {wildcard.summary && <p className="text-sm text-slate-600 leading-relaxed ml-12" style={{ fontFamily: 'Barlow, sans-serif' }}>{wildcard.summary}</p>}
                                            </div>
                                        )}
                                        <div className="flex justify-center pt-4 pb-24 px-4" data-testid="candidate-shortview-card">
                                            <ShortviewTalentCard candidate={candidateForCard} isWildcard={isWildcard} alignmentScore={null} />
                                        </div>
                                        <div className="relative z-10 mt-2 text-center">
                                            <button
                                                className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-base font-bold text-white transition-all duration-200 hover:opacity-90 hover:-translate-y-0.5 shadow-lg"
                                                style={{ background: 'linear-gradient(135deg, #9333EA, #7C3AED)' }}
                                                onClick={() => setShowFullBreakdown(!showFullBreakdown)}
                                                data-testid="see-full-breakdown-btn"
                                            >
                                                {showFullBreakdown ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                                                {showFullBreakdown ? 'Hide Full Breakdown' : 'See Full Breakdown'}
                                            </button>
                                        </div>
                                        {showFullBreakdown && (
                                            <div className="mt-4 bg-white rounded-2xl p-4 sm:p-8 border border-purple-200/50 shadow-sm">
                                                <TalentCard talentCard={talentCard} showFullCard={true} candidateName={user?.name} viewMode="candidate" />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* #4 + #13: Pending Invitations section with id for scroll target */}
                <div id="section-pending" className="mb-8">
                    {pendingTrials.length > 0 && (
                        <>
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#EC4899] block mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            Action Required
                        </span>
                        <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Pending Invitations</h2>
                        <div className="grid grid-cols-1 gap-4">
                            {pendingTrials.map(trial => renderTrialCard(trial, true))}
                        </div>
                        </>
                    )}
                </div>

                {/* #4 + #13: Active Trials section */}
                <div id="section-active" className="mb-8">
                    {activeTrials.length > 0 && (
                        <>
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            In Progress
                        </span>
                        <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Active Trials</h2>
                        <div className="grid grid-cols-1 gap-4">
                            {activeTrials.map(trial => renderTrialCard(trial))}
                        </div>
                        </>
                    )}
                </div>

                {/* #4 + #13: Completed Trials section */}
                <div id="section-completed" className="mb-8">
                    {completedTrials.length > 0 && (
                        <>
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#10B981] block mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            History
                        </span>
                        <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Completed Trials</h2>
                        <div className="grid grid-cols-1 gap-4">
                            {completedTrials.map(trial => renderTrialCard(trial))}
                        </div>
                        </>
                    )}
                </div>

                {/* Brand message */}
                <div className="rounded-2xl p-8 text-center" style={{ background: 'linear-gradient(135deg, #9333EA 0%, #7C3AED 50%, #6D28D9 100%)' }} data-testid="talent-live-message">
                    <p className="text-white text-lg font-medium leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                        Your Talent Card is live! Employers in your sector will discover candidates like you.
                    </p>
                </div>
                </>)}
            </main>
            
            {/* Edit Booking Modal */}
            {editBookingModal && (
                <EditBookingModal
                    booking={editBookingModal}
                    showMessageField
                    submitting={editing}
                    onClose={() => setEditBookingModal(null)}
                    onSubmit={handleEditSubmit}
                />
            )}

            {/* Cancel Trial Modal */}
            {cancelModal && (() => {
                const startDt = cancelModal.start_date ? new Date(cancelModal.start_date) : null;
                const hoursUntilStart = startDt ? Math.max((startDt.getTime() - Date.now()) / 36e5, 0) : null;
                const within24 = hoursUntilStart !== null && hoursUntilStart < 24 && cancelModal.status === 'scheduled';
                return (
                    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => !cancelling && setCancelModal(null)}>
                        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="candidate-cancel-modal">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                    <XCircle className="h-5 w-5 text-amber-600" />
                                </div>
                                <h3 className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Cancel trial</h3>
                            </div>
                            <p className="text-sm text-slate-600 mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                You're about to cancel your trial at <strong>{cancelModal.company_name || 'this employer'}</strong> for <strong>{cancelModal.job_title || 'this role'}</strong>.
                            </p>
                            <div className="rounded-xl bg-purple-50 border border-purple-200/60 p-4 mb-4 text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                <p className="font-semibold text-[#0A0A0A] mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>Cancellation policy</p>
                                <ul className="list-disc list-inside space-y-1 text-slate-600">
                                    <li>24+ hours notice — employer receives a full refund.</li>
                                    <li>Within 24 hours — no refund. A late cancellation is recorded on your account and may lead to suspension.</li>
                                </ul>
                                {within24 && (
                                    <p className="mt-3 text-red-600 font-semibold" data-testid="candidate-cancel-late-warning">
                                        Heads up: this is within 24 hours of the trial start.
                                    </p>
                                )}
                            </div>
                            <div className="flex justify-end space-x-3">
                                <Button variant="outline" onClick={() => setCancelModal(null)} className="rounded-full" disabled={cancelling}>
                                    Keep booking
                                </Button>
                                <Button onClick={handleCancelConfirm} disabled={cancelling}
                                    className="rounded-full bg-red-500 hover:bg-red-600 text-white"
                                    data-testid="confirm-candidate-cancel-btn">
                                    {cancelling ? 'Cancelling…' : 'Cancel trial'}
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Delete Account Modal */}
            {showDeleteAccount && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setShowDeleteAccount(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="delete-account-modal">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <Trash2 className="h-5 w-5 text-red-500" />
                            </div>
                            <h3 className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Delete Account</h3>
                        </div>
                        <p className="text-slate-500 mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            Are you sure you want to permanently delete your account? This will remove all your data including your Talent Card, assessment, and trial history. This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <Button variant="outline" onClick={() => setShowDeleteAccount(false)} className="rounded-full">Cancel</Button>
                            <Button onClick={handleDeleteAccount} disabled={deletingAccount}
                                className="rounded-full bg-red-500 hover:bg-red-600 text-white"
                                data-testid="confirm-delete-account-btn">
                                {deletingAccount ? 'Deleting...' : 'Delete My Account'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes cardFlip {
                    0% { transform: perspective(800px) rotateY(0deg) scale(0.9); opacity: 0.7; }
                    50% { transform: perspective(800px) rotateY(180deg) scale(1.05); opacity: 0.5; }
                    100% { transform: perspective(800px) rotateY(360deg) scale(1); opacity: 1; }
                }
                @keyframes sampler-pulse {
                    0%, 100% { opacity: 0.5; transform: scale(1); }
                    50% { opacity: 1; transform: scale(1.08); }
                }
                @keyframes sampler-loadbar {
                    0% { width: 0%; }
                    30% { width: 35%; }
                    65% { width: 68%; }
                    85% { width: 82%; }
                    100% { width: 100%; }
                }
                @keyframes sampler-spinreveal {
                    0% { transform: perspective(1200px) rotateY(-180deg) scale(0.8); opacity: 0; }
                    60% { transform: perspective(1200px) rotateY(10deg) scale(1.05); opacity: 1; }
                    100% { transform: perspective(1200px) rotateY(0deg) scale(1); opacity: 1; }
                }
            `}</style>

            {assessmentStatus?.completed && <FoundingMemberFAB />}

            {/* Stripe Connect Setup Modal */}
            {connectModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="connect-modal-overlay">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="connect-modal">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                                <CreditCard className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: 'Unbounded, sans-serif' }}>Set up payout details</h2>
                                <p className="text-xs text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>Powered by Stripe</p>
                            </div>
                        </div>

                        <p className="text-sm text-slate-600 mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            Before accepting a trial you need to connect your bank account so Sampler can pay you once your trial is complete.
                        </p>

                        {/* Part 4 copy */}
                        <div className="rounded-xl bg-amber-50 border border-amber-200/60 p-4 mb-4 text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            <p className="font-semibold text-[#0A0A0A] mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>Before you head to Stripe</p>
                            <p className="text-slate-700 leading-relaxed">
                                They'll ask a couple of standard questions — your <strong>business type</strong> (select <strong>Individual / Sole Trader</strong>) and how you earn money (select <strong>Add product description</strong> and write <em>"Paid trial shift work"</em>). Takes under a minute, then straight to entering your bank details for receiving payment.
                            </p>
                        </div>

                        <div className="rounded-xl bg-purple-50 border border-purple-200/60 p-4 mb-5 text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            <p className="text-slate-600">It takes about 5 minutes. You'll be redirected to Stripe and brought straight back here once you're done.</p>
                            {connectStatus?.status === 'pending' && (
                                <p className="mt-2 text-amber-700 font-medium">Your setup is incomplete — click below to finish.</p>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <Button variant="outline" className="flex-1 rounded-full border-purple-200"
                                onClick={() => setConnectModal(false)} data-testid="connect-modal-dismiss">
                                Not now
                            </Button>
                            <Button
                                className="flex-1 rounded-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                                onClick={handleConnectOnboard}
                                disabled={connectLoading}
                                data-testid="connect-modal-submit"
                            >
                                {connectLoading ? 'Redirecting…' : 'Set up payout details'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <PageFooter />
        </div>
    );
};

export default CandidateDashboard;
